import {Injectable} from '@angular/core';
import {Offer} from '../../models/offer/offer.model';
import {OfferRecommendation, OfferRecommendationsApiService} from './offer-recommendations-api.service';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {isNext} from '../../utils/utils';
import {OfferRecommendationConfig, OfferRecommendationRoleConfig} from '../../models/offer/offer-recommendation-config';
import {OfferRecommendationRole, OfferRecommendationType} from '../../consts/offer-recommendation-type.enum';
import {OfferService} from './offer.service';
import {NextOffersSectionStateService} from './section-services/next-offers/next-offers-section-state.service';
import {ApplicationStateService} from '../../../shared/services/application-state.service';
import {FiltersService} from '../../../shared/services/filters.service';
import * as _ from 'lodash';
import {ScrollHelper} from '../../../shared/services/scroll-helper';

@Injectable({
  providedIn: 'root'
})
export class OfferRecommendationsService {

  public recommendations: OfferRecommendation[];
  public readonly missingRecommendationTilesEnabled: boolean = false;
  isNext = isNext();
  private updateInterval: any;
  private wsOpenedTimeout: any;
  private readonly TRANSLATIONS_CONTAINER = 'SHARED.OFFER_RECOMMENDATION_TYPE';
  private readonly UPDATE_INTERVAL_SECS = 5;
  private readonly MIN_RECOMMENDATIONS_AMOUNT = 2;

  private readonly CONFIG: OfferRecommendationConfig[] = [
    {
      type: OfferRecommendationType.CHEAPEST,
      title: `${this.TRANSLATIONS_CONTAINER}.cheapest`,
      nonAcRole: OfferRecommendationRole.CHEAPEST
    },
    {
      type: OfferRecommendationType.BASIC_OC,
      title: `${this.TRANSLATIONS_CONTAINER}.basicOc`,
      nonAcRole: OfferRecommendationRole.POPULAR
    },
    {
      type: OfferRecommendationType.OPTIMUM_OC,
      title: `${this.TRANSLATIONS_CONTAINER}.optimum`,
      nonAcRole: OfferRecommendationRole.FOR_DEMANDING
    },
    {
      type: OfferRecommendationType.BASIC_AC,
      title: `${this.TRANSLATIONS_CONTAINER}.cheapestAc`,
      acRole: OfferRecommendationRole.CHEAPEST
    },
    {
      type: OfferRecommendationType.OPTIMUM_AC,
      title: `${this.TRANSLATIONS_CONTAINER}.basicAc`,
      acRole: OfferRecommendationRole.POPULAR
    },
    {
      type: OfferRecommendationType.FULL,
      title: `${this.TRANSLATIONS_CONTAINER}.complex`,
      acRole: OfferRecommendationRole.FOR_DEMANDING
    },
  ];

  private ROLES_CONFIG: OfferRecommendationRoleConfig[] = [
    {
      role: OfferRecommendationRole.CHEAPEST,
      cssClassSuffix: 'cheapest'
    },
    {
      role: OfferRecommendationRole.POPULAR,
      cssClassSuffix: 'popular'
    },
    {
      role: OfferRecommendationRole.FOR_DEMANDING,
      cssClassSuffix: 'for-demanding'
    }
  ];

  get offers(): Offer[] {
    return this.offerSectionStateService.offers ? this.offerSectionStateService.offers : [];
  }

  get recommendationsActive(): boolean {
    if (!!this.recommendations) {
      return this.activeRecommendationsWithOffer && this.activeRecommendationsWithOffer.length >= this.MIN_RECOMMENDATIONS_AMOUNT;
    }
  }

  get activeRecommendationsWithOffer(): OfferRecommendation[] {
    return this.activeRecommendations ? this.activeRecommendations.filter(recommendation => !!recommendation.offer) : [];
  }

  get activeRecommendations(): OfferRecommendation[] {
    return this.ROLES_CONFIG.map(roleConfig => {
      const config = this.getRoleRecommendationTypeConfig(roleConfig.role);
      const recommendation = this.recommendations.find(item => item.recommendationType === config.type);
      return recommendation ? recommendation : {
        recommendationType: config.type
      }
    });
  }

  constructor(private offerSectionStateService: NextOffersSectionStateService,
              private offerService: OfferService,
              private filtersService: FiltersService,
              private stateService: ApplicationStateService,
              private api: OfferRecommendationsApiService) { }

  init() {
    this.recommendations = [];
    this.clearUpdateInterval();
    this.clearWebsocketOpenedTimeout();

    if (this.isNext) {
      this.offerService.offerDialogService.dialogClosed.take(1).subscribe(() => {
        this.clearUpdateInterval();
        this.updateRecommendedOffers();
        (new ScrollHelper()).scrollToTop();
        this.updateInterval = setInterval(() => {
          this.updateRecommendedOffers();
          if (this.stateService.allOffersReady) {
            this.clearUpdateInterval();
          }
          if (!this.offerService.wsConnected) {
            this.clearUpdateInterval();
          }
        }, this.UPDATE_INTERVAL_SECS * 1000);
      });

      // cancel updates after websocket opened time is up
      this.wsOpenedTimeout = setTimeout(() => {
        this.clearUpdateInterval();
      }, this.offerService.wsOpenedTime);
    }
  }

  clearUpdateInterval(): void {
    if (isNotNullOrUndefined(this.updateInterval)) {
      clearInterval(this.updateInterval);
    }
  }

  clearWebsocketOpenedTimeout(): void {
    if (isNotNullOrUndefined(this.wsOpenedTimeout)) {
      clearTimeout(this.wsOpenedTimeout);
    }
  }

  getConfig(type: OfferRecommendationType): OfferRecommendationConfig {
    return this.CONFIG.find(config => config.type === type);
  }

  getOfferRecommendationType(offer: Offer): OfferRecommendationType | null {
    if (this.recommendations && !!this.recommendations.length) {
      const recommendation = this.recommendations.find(item => item.offerId === offer.id);
      return recommendation ? recommendation.recommendationType : null;
    }
    return null;
  }

  getRoleConfig(type: OfferRecommendationType): OfferRecommendationRoleConfig {
    const recommendationConfig = this.CONFIG.find(config => config.type === type);
    const role = this.filtersService.acSelected ? recommendationConfig.acRole : recommendationConfig.nonAcRole;
    return this.ROLES_CONFIG.find(roleConfig => roleConfig.role === role);
  }

  private getRoleRecommendationTypeConfig(role: OfferRecommendationRole): OfferRecommendationConfig {
    return this.filtersService.acSelected ? this.CONFIG.find(config => config.acRole === role) : this.CONFIG.find(config => config.nonAcRole === role);
  }

  private areNewRecommendationsDifferent(recommendations: OfferRecommendation[]): boolean {
    const currentRawRecommendations = this.recommendations.map(recommendation => ({
      recommendationType: recommendation.recommendationType,
      offerId: recommendation.offerId,
    }));
    return !_.isEqual(currentRawRecommendations, recommendations);
  }

  private async updateRecommendedOffers() {
    const recommendations = await this.api.getRecommendations();
    if (this.areNewRecommendationsDifferent(recommendations)) {
      this.recommendations = recommendations;
      this.recommendations.forEach(recommendation => {
        if (isNotNullOrUndefined(recommendation.offerId)) {
          recommendation.offer = this.offerSectionStateService.getOfferById(recommendation.offerId);
        }
      });
    }
  }
}
